// Data/Forms/Bookings/table.jsx
import { AutoComplete, Input, Select } from "antd";
import React, { createRef, useEffect, useRef, useState } from "react";
import { DeleteOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import AddButton from "../../../../Componets/Buttons/add";
import { select_search } from "../../../../Controllers/Global";
import moment from "moment";
import Error from "../../../../Componets/Error";
import Modalcomponent from "../../../../Componets/Modal";
import { get_inventory } from "../../../../Controllers/Inventory/Batch";
import TextArea from "antd/es/input/TextArea";
import UserCurrency from "../../../../Componets/UserCurrency";

const Bookingtable = React.forwardRef((props) => {
  const { details, setDetails, totals, setTotals, note, setNote } = props;

  const focus = useRef({});

  const [open, setOpen] = useState(false);
  const [modalData, setModalData] = useState();
  const [selectedModalData, setSelectedModalData] = useState();
  const [taxOptions, setTaxOptions] = useState({});

  // ── tax options ──────────────────────────────────────────────────────────
  const fetchTaxOptions = async (category, index) => {
    if (!category) return;
    try {
      const search = await select_search({
        api: "get-all-taxes",
        search: "",
        status: 1,
        source: "Quotation",
        category: category,
      });

      if (search?.status || search?.success) {
        const options = search?.data?.data?.map((v) => ({
          value: v?._id,
          label: v?.name,
          rate: v?.rate,
        }));

        setTaxOptions((prev) => ({ ...prev, [category]: options }));

        const data = [...details];
        if (data[index]?.tax_category === category) {
          data[index].tax_options = options;
          setDetails(data);
        }
      }
    } catch (error) {
      console.error("Error fetching tax options:", error);
    }
  };

  const fetchCategoryForExistingData = async () => {
    if (!details || details.length === 0) return;
    const updatedDetails = [...details];
    let needsUpdate = false;

    for (let i = 0; i < updatedDetails.length; i++) {
      const item = updatedDetails[i];
      if (item.description && !item.tax_category) {
        try {
          const inventory = await get_inventory({ id: item.description });
          if (inventory?.status && inventory?.data?.product?.category?._id) {
            updatedDetails[i].tax_category =
              inventory.data.product.category._id;
            needsUpdate = true;
            await fetchTaxOptions(inventory.data.product.category._id, i);
          }
        } catch (error) {
          console.error(`Error fetching category for item ${i}:`, error);
        }
      }
    }

    if (needsUpdate) setDetails(updatedDetails);
  };

  useEffect(() => {
    fetchCategoryForExistingData();
  }, []);

  const getTaxOptionsForRow = (index) => {
    const data = [...details];
    const category = data[index]?.tax_category;
    if (category && taxOptions[category]) return taxOptions[category];
    return data[index]?.tax_options || [];
  };

  // ── available stock helper ───────────────────────────────────────────────
  const getAvailable = (value) => {
    const stock = parseFloat(value?.stock || 0);
    const booked_stock = parseFloat(value?.booked_stock || 0);
    return Math.max(0, stock - booked_stock);
  };

  // ── search inventory ─────────────────────────────────────────────────────
  const handleSearch = async (e, index) => {
    const data = [...details];
    const search = await select_search({
      api: "get-all-inventories",
      search: e,
      status: 1,
      source: "Booking",
    });

    if (search?.status) {
      data[index].options = search?.data?.data?.map((value) => ({
        value: value?._id,
        label: (
          <div>
            <div>{value?.product?.name}</div>
            <div>
              <span>
                <UserCurrency /> {value?.sale_price?.toFixed(3) || "0.000"}
              </span>

              {` (#BATCH-${value?.number})`}
            </div>
            <div className="text-green-600 text-xs">
              Available:{" "}
              {Math.max(0, (value?.stock || 0) - (value?.booked_stock || 0))}
            </div>
            {value?.expiry_date && (
              <div>
                EXPIRY: {moment(value?.expiry_date)?.format("DD-MM-YYYY")}
              </div>
            )}
          </div>
        ),
      }));
      setDetails(data);
    } else {
      toast?.error(search?.message);
    }
  };

  // ── select inventory ─────────────────────────────────────────────────────
  const handleDescription = async (e, index) => {
    const data = [...details];

    if (e) {
      const inventory = await get_inventory({ id: e });

      if (inventory?.status) {
        // check expiry
        if (
          inventory?.data?.expiry_date &&
          new Date(inventory?.data?.expiry_date) < new Date()
        ) {
          toast?.error(
            `Inventory expired on ${moment(inventory?.data?.expiry_date)?.format("DD-MM-YYYY")}`,
          );
          return;
        }

        if (inventory?.data?.inventory_unit_details?.length > 0) {
          // open unit selection modal
          setOpen(true);
          const modaldata = [
            { ...inventory?.data, name: inventory?.data?.product?.unit?.name },
            ...inventory?.data?.inventory_unit_details,
          ];
          setModalData(modaldata);
          setSelectedModalData({ ...inventory, index });
        } else {
          const description_ids = data.map((v) => v?.description);

          if (!description_ids?.includes(e)) {
            const available =
              (inventory?.data?.stock || 0) -
              (inventory?.data?.booked_stock || 0);

            data[index].description = inventory?.data?._id;
            data[index].name = inventory?.data?.product?.name;
            data[index].batch = inventory?.data?.number;
            data[index].expiry_date = inventory?.data?.expiry_date;
            data[index].unit = inventory?.data?._id;
            data[index].unit_name = inventory?.data?.product?.unit?.name;
            data[index].sale_price = inventory?.data?.wholesale_price
              ? inventory?.data?.wholesale_price
              : inventory?.data?.sale_price;
            data[index].stock = inventory?.data?.stock;
            data[index].booked_stock = inventory?.data?.booked_stock || 0;
            data[index].available = available;
            data[index].tax = inventory?.data?.tax
              ? {
                  id: inventory?.data?.tax?._id,
                  rate: inventory?.data?.tax?.rate,
                  name: inventory?.data?.tax?.name,
                }
              : null;
            data[index].tax_category = inventory?.data?.product?.category?._id;
            data[index].quantity = "";
            data[index].unit_details_options = [];

            if (inventory?.data?.product?.category?._id) {
              fetchTaxOptions(inventory?.data?.product?.category?._id, index);
            }

            setDetails(data);
            focus.current[`quantity${index}`].current.focus();
          } else {
            // already in list — just focus existing row
            const selected_index = description_ids?.indexOf(e);
            setTimeout(() => {
              focus.current[`quantity${selected_index}`].current.focus();
            }, 500);
            setDetails(data);
          }
        }
      } else {
        toast?.error("Inventory not found");
      }
    } else {
      // clear row
      data[index] = {
        ...data[index],
        id: "",
        description: null,
        name: "",
        batch: "",
        expiry_date: "",
        unit: "",
        unit_name: "",
        unit_details_options: null,
        quantity: "",
        sale_price: "",
        stock: "",
        booked_stock: "",
        available: "",
        tax: 0,
        tax_category: "",
        tax_options: null,
        price_per_unit: "",
        tax_amount: "0.000",
        total: 0,
      };
      setDetails(data);
    }
  };

  // ── modal unit selection ─────────────────────────────────────────────────
  const handleModal = (modaldata) => {
    const data = [...details];
    const inventory = selectedModalData;
    const index = selectedModalData?.index;

    let inventory_description = null;
    let inventory_name = "";
    let inventory_batch = "";
    let inventory_expiry_date = "";
    let inventory_unit = "";
    let inventory_unit_name = "";
    let inventory_sale_price = 0;
    let inventory_stock = 0;
    let inventory_booked_stock = 0;
    let inventory_tax = null;
    let inventory_conversion = 0;
    let inventory_unit_details = [];
    let inventory_tax_category = "";

    const description_ids = data.map((v) => v?.unit);

    if (!description_ids?.includes(modaldata)) {
      if (inventory?.data?._id == modaldata) {
        inventory_description = inventory?.data?._id;
        inventory_name = inventory?.data?.product?.name;
        inventory_batch = inventory?.data?.number;
        inventory_expiry_date = inventory?.data?.expiry_date;
        inventory_unit = modaldata;
        inventory_unit_name = inventory?.data?.product?.unit?.name;
        inventory_sale_price = inventory?.data?.wholesale_price
          ? inventory?.data?.wholesale_price
          : inventory?.data?.sale_price;
        inventory_stock = inventory?.data?.stock;
        inventory_booked_stock = inventory?.data?.booked_stock || 0;
        inventory_tax = inventory?.data?.tax;
        inventory_tax_category = inventory?.data?.product?.category?._id;

        inventory?.data?.inventory_unit_details?.forEach((value) => {
          inventory_unit_details.push({
            ...value,
            name: value?.name,
            purchase_price: value?.purchase_price,
            price_per_unit: value?.price_per_unit,
            sale_price: value?.sale_price,
            stock: value?.stock,
            unit_quantity: 0,
          });
        });
      } else {
        const unit_ids =
          inventory?.data?.inventory_unit_details?.map((v) => v?._id) || [];
        const selected_unit = unit_ids?.includes(modaldata)
          ? inventory?.data?.inventory_unit_details?.[
              unit_ids?.indexOf(modaldata)
            ]
          : "";

        inventory_description = inventory?.data?._id;
        inventory_name = inventory?.data?.product?.name;
        inventory_batch = inventory?.data?.number;
        inventory_expiry_date = inventory?.data?.expiry_date;
        inventory_unit = modaldata;
        inventory_unit_name = selected_unit?.name;
        inventory_sale_price = selected_unit?.sale_price;
        inventory_stock = selected_unit?.stock;
        inventory_booked_stock = inventory?.data?.booked_stock || 0;
        inventory_tax = inventory?.data?.tax;
        inventory_conversion = selected_unit?.conversion;
        inventory_tax_category = inventory?.data?.product?.category?._id;
      }

      data[index].description = inventory_description;
      data[index].name = inventory_name;
      data[index].batch = inventory_batch;
      data[index].expiry_date = inventory_expiry_date;
      data[index].unit = inventory_unit;
      data[index].unit_name = inventory_unit_name;
      data[index].quantity = "";
      data[index].sale_price = inventory_sale_price;
      data[index].stock = inventory_stock;
      data[index].booked_stock = inventory_booked_stock;
      data[index].available = Math.max(
        0,
        inventory_stock - inventory_booked_stock,
      );
      data[index].tax = inventory_tax;
      data[index].tax_category = inventory_tax_category;
      data[index].conversion = inventory_conversion;
      data[index].unit_details_options = inventory_unit_details;

      if (inventory_tax_category)
        fetchTaxOptions(inventory_tax_category, index);

      focus.current[`quantity${index}`].current.focus();
      setDetails(data);
      setModalData();
      setSelectedModalData();
    } else {
      // already in list — just focus
      const selected_index = description_ids?.indexOf(modaldata);
      setTimeout(() => {
        focus.current[`quantity${selected_index}`].current.focus();
      }, 500);
      setDetails(data);
      setModalData();
      setSelectedModalData();
    }
  };

  // ── tax search ───────────────────────────────────────────────────────────
  const handleTaxSearch = async (e, index) => {
    const data = [...details];
    const category = data[index]?.tax_category;

    const search = await select_search({
      api: "get-all-taxes",
      search: e,
      status: 1,
      source: "Inventory",
      category: category || "",
    });

    if (search?.status || search?.success) {
      const options = search?.data?.data?.map((v) => ({
        value: v?._id,
        label: v?.name,
        rate: v?.rate,
      }));
      data[index].tax_options = options;
      setDetails(data);
      setTaxOptions((prev) => ({ ...prev, [category]: options }));
    }
  };

  // ── totals calculation ───────────────────────────────────────────────────
  const handleTotal = (e, index, key) => {
    let name = key ? key : e?.target?.name;
    let value = key && e ? e : !key && e?.target?.value ? e?.target?.value : "";

    if (name === "tax" && e && taxOptions) {
      const allTaxOptions = Object.values(taxOptions).flat();
      const matchedTax = allTaxOptions.find((t) => t.value === e);
      if (matchedTax) {
        value = {
          id: matchedTax.value,
          name: matchedTax.label,
          rate: matchedTax.rate,
        };
      }
    }

    if (value >= 0 || (name === "tax" && value)) {
      const data = [...details];

      // ── KEY DIFFERENCE: check against available (stock - booked_stock) ──
      const available = getAvailable(data[index]);

      if (name === "quantity" && parseFloat(value || 0) > available) {
        toast?.error(
          `Available stock: ${available} (Stock: ${data[index]?.stock}, Booked: ${data[index]?.booked_stock})`,
        );
        return;
      }

      const data_sale_price = data[index]?.sale_price || 0;
      const data_discount_percentage = data[index]?.discount_percentage || 0;
      const data_quantity = data[index]?.quantity || 0;
      const data_tax = data[index]?.tax || null;
      const data_conversion = data[index]?.conversion || 0;

      const item_sale_price = name === "sale_price" ? value : data_sale_price;
      const quantity = name === "quantity" ? value : data_quantity;
      const tax = name === "tax" ? value : data_tax;

      const discount_price =
        (parseFloat(item_sale_price || 0) *
          parseFloat(data_discount_percentage || 0)) /
        100;

      const sale_price =
        parseFloat(item_sale_price) - parseFloat(discount_price);
      const tax_amount = tax
        ? parseFloat(sale_price) *
          parseFloat(quantity || 0) *
          (parseFloat(tax?.rate || 0) / 100)
        : 0;
      const total =
        parseFloat(sale_price) * parseFloat(quantity || 0) +
        parseFloat(tax_amount);
      const price_per_unit = parseFloat(total || 0) / parseFloat(quantity || 0);

      const unit_details_options =
        data?.[index]?.unit_details_options?.map((v) => ({
          ...v,
          price_per_unit: price_per_unit
            ? parseFloat(price_per_unit || 0) / parseFloat(v?.conversion || 0)
            : 0,
          unit_quantity: quantity
            ? parseFloat(v?.conversion || 0) * parseFloat(quantity || 0)
            : 0,
        })) || [];

      if (
        data_conversion > 0 &&
        parseFloat(quantity) >= data_conversion * data[index]?.stock
      ) {
        toast?.error(`Quantity exceeding ${data[index].conversion}`);
        return;
      }

      data[index][name] = value;
      data[index].discount_price = discount_price || "";
      data[index].unit_details_options = unit_details_options;
      data[index].tax = tax || 0;
      data[index].total = total || 0;
      data[index].tax_amount = tax_amount || "0.000";
      data[index].price_per_unit = price_per_unit || 0;

      const subtotal =
        data.reduce((acc, v) => acc + parseFloat(v?.total || 0), 0) -
        data.reduce((acc, v) => acc + parseFloat(v?.tax_amount || 0), 0);
      const taxamount = data.reduce(
        (acc, v) => acc + parseFloat(v?.tax_amount || 0),
        0,
      );
      const grandtotal =
        data.reduce((acc, v) => acc + parseFloat(v?.total || 0), 0) -
        parseFloat(totals?.discount || 0);

      totals.subtotal = subtotal;
      totals.taxamount = taxamount;
      totals.total = grandtotal;

      setDetails(data);
      setTotals({ ...totals });
    }
  };

  // ── discount ─────────────────────────────────────────────────────────────
  const handleDiscountPrice = (e, index, key) => {
    const name = key ? key : e?.target?.name;
    const value = e?.target?.value || "";

    if (value >= 0) {
      const data = [...details];

      if (
        parseFloat(value || 0) <= parseFloat(data[index]?.sale_price || 0) ||
        name === "discount_percentage"
      ) {
        if (name === "discount_percentage" && parseFloat(value || 0) > 100) {
          toast?.error("Discount exceeding 100%");
          return;
        }

        let total_discount_price = 0;
        let total_discount_percentage = 0;

        if (parseFloat(value || 0) > 0) {
          const sale_price = parseFloat(data[index]?.sale_price || 0);
          const discount_value = parseFloat(value || 0);

          if (name === "discount_percentage") {
            total_discount_price = (sale_price * discount_value) / 100;
            total_discount_percentage = discount_value;
          } else {
            total_discount_price = discount_value;
            total_discount_percentage = (discount_value / sale_price) * 100;
          }
        }

        const data_sale_price = data[index]?.sale_price || 0;
        const data_quantity = data[index]?.quantity || 0;
        const data_tax = data[index]?.tax || 0;

        const discount_price = total_discount_price;
        const discount_pct = total_discount_percentage;
        const sale_price =
          parseFloat(data_sale_price || 0) - parseFloat(discount_price || 0);

        let tax_percentage = 0;
        if (typeof data_tax === "object" && data_tax?.rate) {
          tax_percentage = data_tax.rate;
        } else {
          tax_percentage = data_tax;
        }

        const tax_amount = tax_percentage
          ? parseFloat(sale_price) *
            parseFloat(data_quantity || 0) *
            (parseFloat(tax_percentage) / 100)
          : 0;
        const total =
          parseFloat(sale_price) * parseFloat(data_quantity || 0) +
          parseFloat(tax_amount);
        const price_per_unit =
          parseFloat(total || 0) / parseFloat(data_quantity || 0);

        data[index].discount_price = discount_price || "";
        data[index].discount_percentage = discount_price ? discount_pct : "";
        data[index].total = total || 0;
        data[index].tax_amount = tax_amount || "0.000";
        data[index].price_per_unit = price_per_unit || 0;

        const subtotal =
          data.reduce((acc, v) => acc + parseFloat(v?.total || 0), 0) -
          data.reduce((acc, v) => acc + parseFloat(v?.tax_amount || 0), 0);
        const taxamount = data.reduce(
          (acc, v) => acc + parseFloat(v?.tax_amount || 0),
          0,
        );
        const grandtotal =
          data.reduce((acc, v) => acc + parseFloat(v?.total || 0), 0) -
          parseFloat(totals?.discount || 0);

        totals.subtotal = subtotal;
        totals.taxamount = taxamount;
        totals.total = grandtotal;

        setDetails(data);
        setTotals({ ...totals });
      } else {
        toast?.error("Exceeding sale price");
      }
    }
  };

  // ── remove row ───────────────────────────────────────────────────────────
  const handleRemove = (index) => {
    const data = [...details];

    data[index].quantity = 0;
    data[index].total = 0;
    data[index].tax_amount = "0.000";

    const subtotal =
      data.reduce((acc, v) => acc + parseFloat(v?.total || 0), 0) -
      data.reduce((acc, v) => acc + parseFloat(v?.tax_amount || 0), 0);
    const taxamount = data.reduce(
      (acc, v) => acc + parseFloat(v?.tax_amount || 0),
      0,
    );
    const grandtotal =
      data.reduce((acc, v) => acc + parseFloat(v?.total || 0), 0) -
      parseFloat(totals?.discount || 0);

    totals.subtotal = subtotal;
    totals.taxamount = taxamount;
    totals.total = grandtotal;

    data.splice(index, 1);
    setDetails(data);
    setTotals({ ...totals });
  };

  // ── add row ──────────────────────────────────────────────────────────────
  const handleAdd = () => {
    setDetails((prev) => [
      ...prev,
      {
        id: "",
        description: null,
        name: "",
        unit: "",
        unit_name: "",
        unit_details_options: null,
        quantity: "",
        sale_price: "",
        stock: "",
        booked_stock: "",
        available: "",
        tax: 0,
        price_per_unit: "",
        expiry_date: "",
        tax_amount: "0.000",
        total: 0,
      },
    ]);

    setTimeout(() => {
      focus.current[`description${details?.length}`].current.focus();
    }, 100);
  };

  // ── footer discount ──────────────────────────────────────────────────────
  const handleDiscount = (e) => {
    if (e?.target?.value >= 0) {
      const total_amount = parseFloat(
        details.reduce((acc, v) => acc + parseFloat(v?.total || 0), 0),
      );
      const total = total_amount - parseFloat(e?.target?.value || 0);

      if (e?.target?.value <= total_amount) {
        totals.discount = e?.target?.value;
        totals.total = total;
        setTotals({ ...totals });
      } else {
        toast?.error(`Total amount ${total_amount}`);
      }
    }
  };

  return (
    <div className="overflow-x-auto">
      <Modalcomponent
        open={open}
        setOpen={setOpen}
        modalData={modalData}
        setModalData={setModalData}
        selectedModalData={selectedModalData}
        setSelectedModalData={setSelectedModalData}
        handleModal={handleModal}
      />

      <table className="w-[100%]">
        <thead>
          <tr>
            <td className="border p-4 text-black">S.NO</td>
            <td className="border p-4 text-black">DESCRIPTION</td>
            <td className="border p-4 text-black">UNIT</td>
            <td className="border p-4 text-black">QUANTITY</td>
            <td className="border p-4 text-black">BOOKING&nbsp;PRICE</td>
            <td className="border p-4 text-black">DISC</td>
            <td className="border p-4 text-black">TAX&nbsp;[VAT]</td>
            <td className="border p-4 text-black">TOTAL</td>
            <td className="border p-4 text-black">DELETE</td>
          </tr>
        </thead>

        <tbody>
          {details?.map((value, index) => {
            focus.current[`description${index}`] = createRef();
            focus.current[`sale_price${index}`] = createRef();
            focus.current[`quantity${index}`] = createRef();

            return (
              <React.Fragment key={index}>
                <tr>
                  <td className="border p-4 text-center text-gray-700">
                    {index + 1}
                  </td>

                  {/* Description */}
                  <td className="border p-4">
                    <div className="pt-2">
                      {value?.description ? (
                        <div className="w-[300px]">
                          <div className="text-xs text-gray-900 font-medium">
                            {value?.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {`#BATCH-${value?.batch || ""}`}
                          </div>
                          {value?.expiry_date && (
                            <div className="text-xs text-gray-500">
                              {`Expiry (${moment(value?.expiry_date)?.format("DD-MM-YYYY")})`}
                            </div>
                          )}
                          <div className="pt-2">
                            <TextArea
                              className="w-full text-regular"
                              name="summary"
                              placeholder="Summary"
                              value={value?.summary}
                              autoComplete="off"
                            />
                          </div>
                        </div>
                      ) : (
                        <Select
                          className="w-[300px] text-regular"
                          name="description"
                          ref={focus.current[`description${index}`]}
                          placeholder="Description"
                          allowClear
                          showSearch
                          filterOption={false}
                          value={value?.description}
                          options={value?.options}
                          onSearch={(e) => handleSearch(e, index)}
                          onFocus={() => handleSearch("", index)}
                          onClick={() => handleSearch("", index)}
                          onChange={(e) => handleDescription(e, index)}
                          autoComplete="off"
                        />
                      )}
                    </div>
                    <Error id={`description${index}`} />
                  </td>

                  {/* Unit */}
                  <td className="border p-4 text-center">
                    <div>{value?.unit_name}</div>
                  </td>

                  {/* Quantity — KEY DIFFERENCE: shows all 3 stock values */}
                  <td className="border p-4">
                    <div>
                      {value?.description && (
                        <>
                          <div className="text-xs text-green-500">
                            Stock&nbsp;
                            {Number(value?.stock || 0).toFixed(3)}
                          </div>
                          <div className="text-xs text-orange-500">
                            Booked&nbsp;
                            {Number(value?.booked_stock || 0).toFixed(3)}
                          </div>
                          <div className="text-xs text-blue-500 font-medium">
                            Available&nbsp;
                            {getAvailable(value).toFixed(3)}
                          </div>
                        </>
                      )}
                      <Input
                        type="number"
                        className="w-full text-regular pt-2"
                        name="quantity"
                        ref={focus.current[`quantity${index}`]}
                        placeholder="Quantity"
                        value={value?.quantity}
                        onChange={(e) => handleTotal(e, index)}
                        autoComplete="off"
                      />
                      <Error id={`quantity${index}`} />
                    </div>
                  </td>

                  {/* Booking Price */}
                  <td className="border p-4 text-center">
                    <AutoComplete
                      className="w-[150px] text-regular"
                      name="sale_price"
                      ref={focus.current[`sale_price${index}`]}
                      placeholder="Booking price"
                      allowClear
                      showSearch
                      filterOption={false}
                      value={value?.sale_price}
                      onChange={(e) => handleTotal(e, index, "sale_price")}
                      autoComplete="off"
                    />
                    <Error id={`sale_price${index}`} />
                  </td>

                  {/* Discount */}
                  <td className="border p-4 text-center">
                    <Input
                      type="number"
                      className="w-[100px] text-regular"
                      name="discount_percentage"
                      placeholder="Discount %"
                      value={value?.discount_percentage}
                      suffix="%"
                      onChange={(e) =>
                        handleDiscountPrice(e, index, "discount_percentage")
                      }
                      autoComplete="off"
                    />
                  </td>

                  {/* Tax */}
                  <td className="border p-4 text-center">
                    <div className="pt-7">
                      <Select
                        className="w-[100px] text-regular"
                        placeholder="Tax"
                        allowClear={false}
                        showSearch
                        filterOption={false}
                        options={getTaxOptionsForRow(index)}
                        value={value?.tax?.name || value?.tax}
                        onSearch={(e) => handleTaxSearch(e, index)}
                        onFocus={() => handleTaxSearch("", index)}
                        onClick={() => handleTaxSearch("", index)}
                        onChange={(e) => handleTotal(e, index, "tax")}
                        autoComplete="off"
                      />
                    </div>
                    <div className="pt-2 text-xs">
                      {value?.tax_amount
                        ? parseFloat(value?.tax_amount)?.toFixed(3)
                        : "0.000"}
                    </div>
                  </td>

                  {/* Total */}
                  <td className="border p-4 text-center">
                    {value?.total
                      ? parseFloat(value?.total)?.toFixed(3)
                      : "0.000"}
                  </td>

                  {/* Delete */}
                  <td className="border p-4 text-center">
                    {value?.id ? (
                      <button
                        type="button"
                        className="text-lg text-red-900 cursor-not-allowed"
                        disabled
                      >
                        <DeleteOutlined />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="text-lg text-red-500 cursor-pointer hover:text-red-800"
                        onClick={() => handleRemove(index)}
                      >
                        <DeleteOutlined />
                      </button>
                    )}
                  </td>
                </tr>

                {/* Sub-row: discount price input */}
                <tr>
                  <td className="border border-gray-200 p-4"></td>
                  <td className="border border-gray-200 p-4"></td>
                  <td className="border border-gray-200 p-4"></td>
                  <td className="border border-gray-200 p-4"></td>
                  <td className="border border-gray-200 p-4">
                    {value?.unit_details_options?.map((v, i) => (
                      <div key={i} className="pt-2">
                        <div className="pb-2 text-xs">
                          {v?.name}({v?.conversion})
                        </div>
                        <div className="border border-gray-300 p-1 rounded-md text-xs">
                          {v?.unit_quantity}
                        </div>
                      </div>
                    ))}
                  </td>
                  <td className="border border-gray-200 p-4">
                    <div className="pt-2">
                      <div className="pb-2 text-xs">Disc</div>
                      <Input
                        type="number"
                        className="w-[100px] text-regular"
                        name="discount_price"
                        placeholder="Discount price"
                        value={value?.discount_price}
                        onChange={(e) =>
                          handleDiscountPrice(e, index, "discount_price")
                        }
                        autoComplete="off"
                      />
                    </div>
                  </td>
                  <td className="border border-gray-200 p-4"></td>
                  <td className="border border-gray-200 p-4"></td>
                  <td className="border border-gray-200 p-4"></td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>

        <tfoot>
          <tr>
            <td className="border p-4" rowSpan={2} colSpan={6}></td>
            <td className="border p-4" rowSpan={2} colSpan={3}>
              <div className="text-center">
                <AddButton
                  className="form-save-button w-full h-full text-center"
                  onClick={handleAdd}
                />
              </div>
            </td>
          </tr>
          <tr></tr>
          <tr>
            <td className="border p-4" colSpan={6}></td>
            <td className="border p-4 text-gray-700">SUBTOTAL</td>
            <td className="border p-4" colSpan={2}>
              {totals?.subtotal > 0
                ? parseFloat(totals?.subtotal)?.toFixed(3)
                : "0.000"}
            </td>
          </tr>
          <tr>
            <td className="border p-4" colSpan={6}></td>
            <td className="border p-4 text-gray-700">TAX&nbsp;[VAT]</td>
            <td className="border p-4" colSpan={2}>
              {totals?.taxamount > 0
                ? parseFloat(totals?.taxamount)?.toFixed(3)
                : "0.000"}
            </td>
          </tr>
          <tr>
            <td className="border p-4" colSpan={6}></td>
            <td className="border p-4 text-gray-700">DISCOUNT</td>
            <td className="border p-4" colSpan={2}>
              <Input
                type="number"
                className="w-full"
                placeholder="Discount"
                name="discount"
                value={totals?.discount}
                autoComplete="off"
                onChange={handleDiscount}
              />
            </td>
          </tr>
          <tr>
            <td className="border p-4" colSpan={6}></td>
            <td className="border p-4 text-black font-medium">TOTAL</td>
            <td className="border p-4 text-black font-medium" colSpan={2}>
              {totals?.total > 0
                ? parseFloat(totals?.total)?.toFixed(3)
                : "0.000"}
            </td>
          </tr>
        </tfoot>
      </table>

      {/* Notes */}
      <div className="mt-4">
        <div className="mb-2 text-sm text-gray-600">Notes</div>
        <TextArea
          rows={3}
          placeholder="Additional notes"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </div>
  );
});

export default Bookingtable;
